/**
 * Production Statistics Component
 * Displays production estimation data by province and community
 */

import { Card, Table, Statistic, Row, Col, Spin, Typography, Space, Select } from 'antd';
import { useState, useEffect } from 'react';
import {
  RiseOutlined,
  FallOutlined,
  AreaChartOutlined,
  CheckCircleOutlined,
} from '@ant-design/icons';
import { productionDashboardService, type ProductionDashboardData, type PackerProductionData, type ProductionTypeData, type NetCoverageData } from '../../services/api/productionDashboardService';
import { ProductionHistoryChart } from './ProductionHistoryChart';
import { SurfaceAuditsChart } from './SurfaceAuditsChart';
import { PackerProductionChart } from './PackerProductionChart';
import { ProductionTypeSurfaceChart } from './ProductionTypeSurfaceChart';
import { NetCoverageSurfaceChart } from './NetCoverageSurfaceChart';

const { Text } = Typography;

import { seasonService, type Season } from '../../services/api/seasonService';

interface ProductionStatsProps {}

export const ProductionStats: React.FC<ProductionStatsProps> = () => {
  const [seasons, setSeasons] = useState<Season[]>([]);
  const [selectedSeason, setSelectedSeason] = useState<number | undefined>(undefined);
  const [dashboardData, setDashboardData] = useState<ProductionDashboardData | null>(null);
  const [packerData, setPackerData] = useState<PackerProductionData[]>([]);
  const [productionTypeData, setProductionTypeData] = useState<ProductionTypeData[]>([]);
  const [netCoverageData, setNetCoverageData] = useState<NetCoverageData[]>([]);
  const [isLoading, setIsLoading] = useState(false);

  // Load seasons on mount
  useEffect(() => {
    const loadSeasons = async () => {
      try {
        const seasonsData = await seasonService.getSeasons();
        setSeasons(seasonsData);
        // Select the most recent season by default
        if (seasonsData.length > 0) {
          const sortedSeasons = [...seasonsData].sort((a, b) => 
            b.year - a.year
          );
          setSelectedSeason(sortedSeasons[0].id);
        }
      } catch (error) {
        // Error handled silently
      }
    };
    loadSeasons();
  }, []);

  // Load dashboard data when season changes
  useEffect(() => {
    if (!selectedSeason) return;

    const loadDashboardData = async () => {
      setIsLoading(true);
      try {
        const [dashData, packerRes, prodTypeRes, netCovRes] = await Promise.all([
          productionDashboardService.getDashboardData(
            selectedSeason,
            undefined // No country filter
          ),
          productionDashboardService.getPackerProduction(selectedSeason),
          productionDashboardService.getProductionTypeSurface(selectedSeason),
          productionDashboardService.getNetCoverageSurface(selectedSeason),
        ]);
        
        setDashboardData(dashData);
        setPackerData(packerRes.packers);
        setProductionTypeData(prodTypeRes.production_types);
        setNetCoverageData(netCovRes.net_coverage);
      } catch (error) {
        // Error handled silently
      } finally {
        setIsLoading(false);
      }
    };
    loadDashboardData();
  }, [selectedSeason]);

  const columns = [
    {
      title: 'Provincia',
      dataIndex: 'province_name',
      key: 'province_name',
      sorter: (a: any, b: any) => a.province_name.localeCompare(b.province_name),
    },
    {
      title: 'Región',
      dataIndex: 'region_name',
      key: 'region_name',
      sorter: (a: any, b: any) => a.region_name.localeCompare(b.region_name),
    },
    {
      title: 'País',
      dataIndex: 'country_name',
      key: 'country_name',
      sorter: (a: any, b: any) => a.country_name.localeCompare(b.country_name),
    },
    {
      title: 'Sup. Planificada (ha)',
      dataIndex: 'planned_surface',
      key: 'planned_surface',
      align: 'right' as const,
      render: (value: number) => value.toLocaleString('es-ES', { minimumFractionDigits: 2, maximumFractionDigits: 2 }),
      sorter: (a: any, b: any) => a.planned_surface - b.planned_surface,
    },
    {
      title: 'Sup. Auditada (ha)',
      dataIndex: 'audited_surface',
      key: 'audited_surface',
      align: 'right' as const,
      render: (value: number) => value.toLocaleString('es-ES', { minimumFractionDigits: 2, maximumFractionDigits: 2 }),
      sorter: (a: any, b: any) => a.audited_surface - b.audited_surface,
    },
    {
      title: 'Est. Productor (kg)',
      dataIndex: 'producer_estimation',
      key: 'producer_estimation',
      align: 'right' as const,
      render: (value: number) => value.toLocaleString('es-ES', { minimumFractionDigits: 0, maximumFractionDigits: 0 }),
      sorter: (a: any, b: any) => a.producer_estimation - b.producer_estimation,
    },
    {
      title: 'Est. Auditor (kg)',
      dataIndex: 'auditor_estimation',
      key: 'auditor_estimation',
      align: 'right' as const,
      render: (value: number) => value.toLocaleString('es-ES', { minimumFractionDigits: 0, maximumFractionDigits: 0 }),
      sorter: (a: any, b: any) => a.auditor_estimation - b.auditor_estimation,
    },
    {
      title: 'Crecimiento vs Año Anterior',
      dataIndex: 'growth_percentage',
      key: 'growth_percentage',
      align: 'center' as const,
      render: (value: number) => (
        <Space>
          {value > 0 ? (
            <RiseOutlined style={{ color: '#52c41a' }} />
          ) : value < 0 ? (
            <FallOutlined style={{ color: '#ff4d4f' }} />
          ) : null}
          <Text style={{ color: value > 0 ? '#52c41a' : value < 0 ? '#ff4d4f' : undefined }}>
            {value > 0 ? '+' : ''}{value.toFixed(2)}%
          </Text>
        </Space>
      ),
      sorter: (a: any, b: any) => a.growth_percentage - b.growth_percentage,
    },
    {
      title: 'Auditorías',
      key: 'audits',
      align: 'center' as const,
      render: (_: any, record: any) => (
        <Text>
          {record.completed_audits_count} / {record.audits_count}
        </Text>
      ),
      sorter: (a: any, b: any) => a.completed_audits_count - b.completed_audits_count,
    },
  ];

  return (
    <div>
      {/* Season Selector */}
      <Card style={{ marginBottom: 24 }}>
        <Space direction="vertical" style={{ width: '100%' }}>
          <Text strong>Seleccionar Campaña:</Text>
          <Select
            style={{ width: '100%', maxWidth: 400 }}
            placeholder="Seleccionar campaña"
            value={selectedSeason}
            onChange={setSelectedSeason}
            options={seasons.map(season => ({
              label: season.name || season.year.toString(),
              value: season.id,
            }))}
          />
          {dashboardData?.previous_season && (
            <Text type="secondary">
              Comparando con campaña anterior: {dashboardData.previous_season.name}
            </Text>
          )}
        </Space>
      </Card>

      {isLoading ? (
        <Card>
          <div style={{ textAlign: 'center', padding: '40px 0' }}>
            <Spin size="large" />
            <div style={{ marginTop: 16 }}>
              <Text>Cargando datos de producción...</Text>
            </div>
          </div>
        </Card>
      ) : dashboardData ? (
        <>
          {/* KPIs */}
          <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
            <Col xs={24} sm={12} lg={6}>
              <Card>
                <Statistic
                  title="Superficie"
                  value={dashboardData.totals.audited_surface}
                  precision={2}
                  suffix="ha"
                  prefix={<AreaChartOutlined />}
                />
                <Text type="secondary" style={{ fontSize: 14 }}>
                  {dashboardData.totals.audited_surface.toLocaleString('es-ES', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} / {dashboardData.totals.planned_surface.toLocaleString('es-ES', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} ha
                </Text>
                <br />
                <Text 
                  style={{ 
                    fontSize: 14,
                    color: dashboardData.totals.planned_surface > 0 
                      ? (dashboardData.totals.audited_surface / dashboardData.totals.planned_surface) >= 0.8 
                        ? '#52c41a' 
                        : '#faad14'
                      : undefined
                  }}
                >
                  {dashboardData.totals.planned_surface > 0 
                    ? `${((dashboardData.totals.audited_surface / dashboardData.totals.planned_surface) * 100).toFixed(1)}% completado`
                    : 'N/A'}
                </Text>
              </Card>
            </Col>
            <Col xs={24} sm={12} lg={6}>
              <Card>
                <Statistic
                  title="Nº Auditorías"
                  value={dashboardData.totals.completed_audits_count}
                  prefix={<CheckCircleOutlined />}
                />
                <Text type="secondary" style={{ fontSize: 14 }}>
                  {dashboardData.totals.completed_audits_count} / {dashboardData.totals.audits_count} auditorías
                </Text>
                <br />
                <Text 
                  style={{ 
                    fontSize: 14,
                    color: dashboardData.totals.audits_count > 0 
                      ? (dashboardData.totals.completed_audits_count / dashboardData.totals.audits_count) >= 0.8 
                        ? '#52c41a' 
                        : '#faad14'
                      : undefined
                  }}
                >
                  {dashboardData.totals.audits_count > 0 
                    ? `${((dashboardData.totals.completed_audits_count / dashboardData.totals.audits_count) * 100).toFixed(1)}% completado`
                    : 'N/A'}
                </Text>
              </Card>
            </Col>
            <Col xs={24} sm={12} lg={6}>
              <Card>
                <Statistic
                  title="Estimación Productor"
                  value={dashboardData.totals.producer_estimation}
                  precision={0}
                  suffix="kg"
                  prefix={
                    dashboardData.totals.producer_growth_percentage > 0 ? (
                      <RiseOutlined style={{ color: '#52c41a' }} />
                    ) : dashboardData.totals.producer_growth_percentage < 0 ? (
                      <FallOutlined style={{ color: '#ff4d4f' }} />
                    ) : undefined
                  }
                  valueStyle={{
                    color:
                      dashboardData.totals.producer_growth_percentage > 0
                        ? '#52c41a'
                        : dashboardData.totals.producer_growth_percentage < 0
                        ? '#ff4d4f'
                        : undefined,
                  }}
                />
                <Text
                  type="secondary"
                  style={{
                    fontSize: 14,
                    color:
                      dashboardData.totals.producer_growth_percentage > 0
                        ? '#52c41a'
                        : dashboardData.totals.producer_growth_percentage < 0
                        ? '#ff4d4f'
                        : undefined,
                  }}
                >
                  {dashboardData.totals.producer_growth_percentage > 0 ? '+' : ''}
                  {dashboardData.totals.producer_growth_percentage.toFixed(2)}% vs año anterior
                </Text>
              </Card>
            </Col>
            <Col xs={24} sm={12} lg={6}>
              <Card>
                <Statistic
                  title="Estimación Auditor"
                  value={dashboardData.totals.auditor_estimation}
                  precision={0}
                  suffix="kg"
                  prefix={
                    dashboardData.totals.growth_percentage > 0 ? (
                      <RiseOutlined style={{ color: '#52c41a' }} />
                    ) : dashboardData.totals.growth_percentage < 0 ? (
                      <FallOutlined style={{ color: '#ff4d4f' }} />
                    ) : undefined
                  }
                  valueStyle={{
                    color:
                      dashboardData.totals.growth_percentage > 0
                        ? '#52c41a'
                        : dashboardData.totals.growth_percentage < 0
                        ? '#ff4d4f'
                        : undefined,
                  }}
                />
                <Text
                  type="secondary"
                  style={{
                    fontSize: 14,
                    color:
                      dashboardData.totals.growth_percentage > 0
                        ? '#52c41a'
                        : dashboardData.totals.growth_percentage < 0
                        ? '#ff4d4f'
                        : undefined,
                  }}
                >
                  {dashboardData.totals.growth_percentage > 0 ? '+' : ''}
                  {dashboardData.totals.growth_percentage.toFixed(2)}% vs año anterior
                </Text>
              </Card>
            </Col>
          </Row>

          {/* Table */}
          <Card title="Producción por Provincia y Región">
            <Table
              columns={columns}
              dataSource={dashboardData.provinces}
              rowKey="province_id"
              pagination={{
                pageSize: 20,
                showSizeChanger: true,
                showTotal: (total) => `Total: ${total} provincias`,
              }}
              scroll={{ x: 1200 }}
            />
          </Card>

          {/* Historical Charts */}
          {dashboardData.historical_data && dashboardData.historical_data.length > 0 && (
            <>
              <Row gutter={[16, 16]} style={{ marginTop: 24 }}>
                <Col xs={24} lg={12}>
                  <SurfaceAuditsChart data={dashboardData.historical_data} />
                </Col>
                <Col xs={24} lg={12}>
                  <ProductionHistoryChart data={dashboardData.historical_data} />
                </Col>
              </Row>
            </>
          )}

          {/* New Charts: Packer Production, Production Type, Net Coverage */}
          <Row gutter={[16, 16]} style={{ marginTop: 24 }}>
            <Col xs={24} lg={12}>
              <PackerProductionChart data={packerData} />
            </Col>
            <Col xs={24} lg={12}>
              <Row gutter={[16, 16]}>
                <Col xs={24}>
                  <ProductionTypeSurfaceChart data={productionTypeData} />
                </Col>
                <Col xs={24} style={{ marginTop: 16 }}>
                  <NetCoverageSurfaceChart data={netCoverageData} />
                </Col>
              </Row>
            </Col>
          </Row>
        </>
      ) : (
        <Card>
          <Text type="danger">Error al cargar los datos de producción</Text>
        </Card>
      )}
    </div>
  );
};
